import { ENV_CONFIG } from '../../../../__config__/env';
import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';
import { createGTMEvent } from '../../../../utils/tracking/gtm';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../__types__/generated/GeneralValuation';
import { createLeadMutation } from '../../../../../client/__graphql__/mutations';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';

import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const KampanjeVinnEnMeglerBergen: React.FC = () => {
  const location = useLocation();
  const [showRules, setShowRules] = React.useState(false);
  const { submitWithDigger } = useDigtectiveContext();
  const { routeConfig } = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
            success
          }`
    }
  });

  const [mail, { loading: mailLoading, error }] = useMutation<ILeadResponse>(
    MAIL_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead.mail?.success) {
          setPosted(true);
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresse',
        value: '',
        label: 'Adresse *',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        mail({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: 'EPOST',
              mailType: 131
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Kampanje - Vinn en megler - PrivatMegleren Bergen"
        description="I PrivatMegleren er vi opptatt av å levere de beste resultater på dine vegne."
        image="https://cdn.reeltime.no/pm_assets/kampanjer/vinnenmegler/eiendomsmegler_jaeren_konkurranse.jpg"
        url={location.pathname}
      />
      <Container style={pageFadeIn}>
        {/*         <Top url="https://cdn.reeltime.no/pm_assets/kampanjer/vinnenmegler/eiendomsmegler_jaeren_konkurranse.jpg" /> */}
        <Wrapper
          style={{
            paddingTop: '3em'
          }}
        >
          <FormWrapper style={fadeOut}>
            <Section className="form-section" style={{ paddingBottom: '10px' }}>
              <Heading tag="h1" center={true}>
                Vinn en megler
              </Heading>
              <Paragraph center={true} className="form-text">
                PrivatMegleren kombinerer erfaring med dokumenterbare løsninger
                og teknologi som gir boligselgere et fortrinn. Med vår
                skreddersydde presentasjon av enhver eiendom vi selger, sikrer
                vi at boligen fremstår fra sin beste side når den markedsføres.
              </Paragraph>

              <Paragraph center={true} className="form-text">
                Hele 9 av 10 kunder anbefaler oss videre, og vi velger å tro at
                det er som følge av leveransen vår, og resultatene vi skaper.
              </Paragraph>
              <Paragraph center={true} className="form-text">
                Går du med planer om å selge bolig det neste året? Nå kan du
                vinne et kostnadsfritt boligsalg gjennom oss med verdi på inntil
                kr. 250.000.
              </Paragraph>
              <Paragraph center={true} className="form-text">
                Fyll ut skjemaet under og bli med i trekningen av et boligsalg.
              </Paragraph>

              <ButtonGroup
                position="center"
                style={{ textAlign: 'center', paddingBottom: '1em' }}
              >
                <Button
                  type="tertiary"
                  colorTheme="gold"
                  onClick={() => setShowRules((prev) => !prev)}
                >
                  {showRules
                    ? 'Lukk konkurranseregler'
                    : 'Se konkurranseregler'}
                </Button>
              </ButtonGroup>
              {showRules ? (
                <>
                  <Paragraph
                    style={{ fontWeight: 'bold' }}
                    center={true}
                    className="form-text"
                  >
                    Konkurranseregler
                  </Paragraph>
                  <Paragraph center={true} className="form-text">
                    Premien er et boligsalg med verdi inntil kr 250.000
                    inkludert utlegg, digital annonsering med Presisjon,
                    annonsering avis halvside annonse, hjem.no, finn.no,
                    boligfoto, takst og vederlag for gjennomføring av
                    salgsoppdraget. Vinner trekkes mandag 31. desember 2025.
                    Vedkommende vil bli kontaktet og vinner annonsert på våre
                    Facebook-sider.
                  </Paragraph>
                </>
              ) : null}
            </Section>
            <Form
              style={{ paddingTop: '2em' }}
              onSubmit={handleSubmit}
              noValidate
            >
              {error &&
                error.graphQLErrors.map((error, index) => (
                  <FormError key={`error_${index}`} message={error.message} />
                ))}
              {fields.map((item, index) => {
                return (
                  <FormRow key={`field_${index}`}>
                    <Input
                      type={item?.type}
                      name={item?.name}
                      label={item?.label}
                      labelAfter={item?.labelAfter}
                      placeholder={item?.placeholder}
                      value={item?.value}
                      onChange={handleChange}
                      error={item?.error}
                    />
                  </FormRow>
                );
              })}

              <ButtonGroup>
                <Button
                  type="primary"
                  colorTheme="gold"
                  disabled={loading}
                  loading={loading}
                >
                  Send inn
                </Button>
                <ButtonLink
                  as="a"
                  href={routeConfig?.personvern.url}
                  className="privacy-link-mobile"
                >
                  Personvernpolicy
                </ButtonLink>
              </ButtonGroup>
            </Form>
          </FormWrapper>
          <Thanks style={fadeIn}>
            <Section className="form-section">
              <Heading tag="h1" center={true}>
                Gratulerer
              </Heading>
              <Paragraph center={true}>
                Du er nå med i konkurransen om å vinne et kostnadsfritt
                boligsalg gjennom PrivatMegleren med verdi på inntil kr.
                250.000.
              </Paragraph>
              <Paragraph center={true}>
                Alle som er i kontakt med oss i kampanjeperioden og melder seg
                på konkurransen, uavhengig om de har allerede har signert
                salgsoppdrag med oss eller ikke, er med i trekningen.
              </Paragraph>
              <Paragraph center={true}>
                Vi kontakter deg på mail om det blir ditt forslag som vinner.
              </Paragraph>
              <Paragraph center={true}>
                Dersom du ønsker en verdivurdering av bolig eller et tilbud fra
                oss allerede i dag kan{' '}
                <a href="https://privatmegleren.no/verdivurdering">
                  kontakte oss her.
                </a>
              </Paragraph>
              <Paragraph center={true}>
                Vi ønsker å tilby Basis kjøpsmegling uten kostnad til alle som
                deltar i konkurransen. Ønsker du å snakke med oss om
                kjøpsmegling, les mer og{' '}
                <a href="https://privatmegleren.no/kjop">kontakte oss her.</a>
              </Paragraph>

              <AccordionGeneral />
            </Section>
            <Section className="form-section" style={{ paddingTop: 0 }}>
              <AccordionBenefits />
            </Section>
          </Thanks>
        </Wrapper>
      </Container>
      <BackgroundLoad
        opacity={0.3}
        url={`${ENV_CONFIG.CDN_HOST}privatmegleren/hus.jpg`}
      />
    </>
  );
};

const FormWrapper = styled(animated.div)`
  .bold {
    font-weight: bold;
  }
`;

export default KampanjeVinnEnMeglerBergen;

const Wrapper = styled.div``;
